/**
 * Saved work timezone for working-hours login/logout.
 * Chosen once, reused on every punch, changeable when the employee's
 * work region changes. Not asked again on each punch.
 */

import { getAuthRole } from "./authSession";

export const WORK_TIMEZONE_STORAGE_KEY = "work_timezone_preference";

export const WORK_TIMEZONE_OPTIONS = [
  { id: "Asia/Kolkata", value: "India (IST)", shortLabel: "IST" },
  // { id: "America/Los_Angeles", value: "US (PST)", shortLabel: "US PST" },
];

const ALLOWED = new Set(WORK_TIMEZONE_OPTIONS.map((o) => o.id));

function readMap() {
  if (typeof localStorage === "undefined") return {};
  try {
    const parsed = JSON.parse(
      localStorage.getItem(WORK_TIMEZONE_STORAGE_KEY) || "{}",
    );
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function currentUserId() {
  if (typeof localStorage === "undefined") return "";
  return String(localStorage.getItem("user_id") || "").trim();
}

function readUserData() {
  if (typeof localStorage === "undefined") return {};
  try {
    return JSON.parse(localStorage.getItem("USER_DATA") || "{}");
  } catch {
    return {};
  }
}

export function normalizeWorkTimeZone(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (ALLOWED.has(s)) return s;
  const key = s.toLowerCase().replace(/\s+/g, "_");
  if (
    key === "india" ||
    key === "in" ||
    key === "ist" ||
    key === "asia/kolkata" ||
    key === "asia/calcutta"
  ) {
    return "Asia/Kolkata";
  }
  if (
    key === "us" ||
    key === "usa" ||
    key === "pt" ||
    key === "pst" ||
    key === "pdt" ||
    key === "pacific" ||
    key === "et" ||
    key === "est" ||
    key === "edt" ||
    key === "eastern" ||
    key === "america/los_angeles" ||
    key === "america/new_york"
  ) {
    // US punches/display use Pacific; migrate older America/New_York saves.
    return "America/Los_Angeles";
  }
  return "";
}

export function getSavedWorkTimeZone() {
  // Login uses IST only while US (PST) is commented out of the dropdown.
  return "Asia/Kolkata";
}

export function saveWorkTimeZone(ianaTz) {
  const tz = normalizeWorkTimeZone(ianaTz);
  if (!tz || typeof localStorage === "undefined") return "";

  const uid = currentUserId();
  const map = readMap();
  if (uid) map[uid] = tz;
  localStorage.setItem(WORK_TIMEZONE_STORAGE_KEY, JSON.stringify(map));

  try {
    const user = readUserData();
    const next = {
      ...user,
      work_timezone: tz,
      workTimezone: tz,
    };
    localStorage.setItem("USER_DATA", JSON.stringify(next));
  } catch {
    /* ignore */
  }

  return tz;
}

/** Restore backend / login payload into the saved preference. */
export function hydrateWorkTimeZoneFromUser(user) {
  const tz = normalizeWorkTimeZone(
    user?.work_timezone || user?.workTimezone || user?.work_time_zone,
  );
  if (tz) saveWorkTimeZone(tz);
  return tz || getSavedWorkTimeZone();
}

/**
 * Save locally (immediate) and persist on the employee record when possible
 * so the same timezone is used after login on another browser.
 */
export async function persistWorkTimeZonePreference(ianaTz) {
  const tz = saveWorkTimeZone(ianaTz);
  if (!tz) return { success: false, timezone: "" };

  try {
    const user = readUserData();
    const employeeId =
      user.employee_id || user.employeeId || currentUserId();
    const role = getAuthRole();
    if (employeeId && role !== "client") {
      const { EMPLOYEE_ACTION } = await import("../redux/action");
      await EMPLOYEE_ACTION.insertUpdateEmployee({
        params: { work_timezone: tz },
        insertUpdateFlag: 1,
        employee_id: employeeId,
      });
    }
  } catch {
    /* local save already applied */
  }

  return { success: true, timezone: tz };
}
